export enum Gender {
  MALE = 'male',
  LADY = 'lady',
  EMPTY = '',
}

export const GENDER_STRING = {
  [Gender.MALE]: '男',
  [Gender.LADY]: '女',
  [Gender.EMPTY]: '-',
};

export type TzPlatformCustomer = {
  displayName: string;
  phoneNumber?: string;
  birthday?: string;
  isLoginFormSubmitted: boolean | null;
  tzWallet?: {
    maskedId: string;
    chRfidCode: string;
    // system: TzWalletSystem;
  };
  lineUId?: string;
  containers: Record<string, Record<string, number>>; //{ [containerId]: { [returnDate]: number } }
  containerNodeId: string;
  totalDiscount: number;
};

export enum TZuchiAgeLevel {
  Empty = -1,
  /**
   * 16 - 30 岁
   */
  From16To30 = 1,

  /**
   * 31 - 40 岁
   */
  From31To40 = 2,

  /**
   * 51 - 60 岁
   */
  From41To60 = 3,

  /**
   * 61 岁以上
   */
  From61 = 4,
}

export const AGE_LEVEL_STRING = {
  [TZuchiAgeLevel.Empty]: '-',
  [TZuchiAgeLevel.From16To30]: '16~30',
  [TZuchiAgeLevel.From31To40]: '31~40',
  [TZuchiAgeLevel.From41To60]: '41~60',
  [TZuchiAgeLevel.From61]: '61以上',
};

export enum TZuchiCookingStyle {
  /**
   * 中式料理
   */
  Chinese = 1,

  /**
   * 西式料理
   */
  Western = 2,

  /**
   * 義式料理
   */
  Italian = 3,

  /**
   * 日式料理
   */
  Japanese = 4,

  /**
   * 韓式料理
   */
  Korean = 5,

  /**
   * 東南亞料理
   */
  SoutheastAsia = 6,
}

export const COOKING_STYLE_STRING: Record<string, string> = {
  [TZuchiCookingStyle.Chinese]: '中式',
  [TZuchiCookingStyle.Western]: '西式',
  [TZuchiCookingStyle.Italian]: '義式',
  [TZuchiCookingStyle.Japanese]: '日式',
  [TZuchiCookingStyle.Korean]: '韓式',
  [TZuchiCookingStyle.SoutheastAsia]: '東南亞',
};

/**
 * 辣度口味偏好
 */
export enum TZuchiChilliTaste {
  /**
   * 香辣
   */
  Spicy = 1,

  /**
   * 微辣
   */
  Mild = 2,

  /**
   * 不辣
   */
  NotSpicy = 3,
}

export const CHILLI_TASTE_STRING: Record<string, string> = {
  [TZuchiChilliTaste.NotSpicy]: '不辣',
  [TZuchiChilliTaste.Mild]: '微辣',
  [TZuchiChilliTaste.Spicy]: '香辣',
};

/**
 * 酸度口味偏好
 */
export enum TZuchiSourTaste {
  /**
   * 酸味濃
   */
  Sour = 1,

  /**
   * 清爽酸
   */
  LitteSour = 2,
}

export const TZUCHI_SOUR_STRING: Record<string, string> = {
  [TZuchiSourTaste.Sour]: '酸味濃',
  [TZuchiSourTaste.LitteSour]: '清爽酸',
};

/**
 * 口味偏好
 */
export enum TZuchiTaste {
  /**
   * 重口味
   */
  Strong = 1,

  /**
   * 清淡
   */
  Light = 2,
}

export const TZUCHI_TASTE_STRING: Record<string, string> = {
  [TZuchiTaste.Strong]: '重口味',
  [TZuchiTaste.Light]: '清淡',
};

/**
 * 饮食风格
 */
export enum TZuchiEatingStyle {
  /**
   * 高蛋白低碳水
   */
  HighProteinLowCarb = 1,

  /**
   * 高纖維（蔬果比例較高）
   */
  HighFiber = 2,

  /**
   * 低油低鹽
   */
  LowFatLowSalt = 3,

  /**
   * 無麩質選項
   */
  GlutenFree = 4,

  /**
   * 素食快餐（如素料類漢堡、三明治）
   */
  VegetarianFastFood = 5,
}

export const TZUCHI_EATING_STRING: Record<string, string> = {
  [TZuchiEatingStyle.HighProteinLowCarb]: '高蛋白低碳水',
  [TZuchiEatingStyle.HighFiber]: '高纖維',
  [TZuchiEatingStyle.LowFatLowSalt]: '低油低鹽',
  [TZuchiEatingStyle.GlutenFree]: '無麩質',
  [TZuchiEatingStyle.VegetarianFastFood]: '素食快餐',
};

export const TZUCHI_EATING_OPTIONS_STRING: Record<string, string> = {
  [TZuchiEatingStyle.HighProteinLowCarb]: '高蛋白低碳水',
  [TZuchiEatingStyle.HighFiber]: '高纖維（蔬果比例較高）',
  [TZuchiEatingStyle.LowFatLowSalt]: '低油低鹽',
  [TZuchiEatingStyle.GlutenFree]: '無麩質',
  [TZuchiEatingStyle.VegetarianFastFood]: '素食快餐（如素料類漢堡、三明治）',
};

/**
 * 慈济项目，进行一些详细信息之后的数据结构
 *
 *  姓名 displayName
 *  手机 phoneNumber
 *  性别 gender
 *  年龄 age
 *  电子信箱 email
 *
 */
export type Customer = TzPlatformCustomer & {
  // 性别
  gender: Gender;

  // 年龄
  age: TZuchiAgeLevel;

  // 邮箱
  email?: string;

  // 較喜歡的料理方式（可複選）
  cookingStyle?: TZuchiCookingStyle[];

  // 口味偏好 (香辣 / 微辣 / 不辣)
  chilliTaste?: TZuchiChilliTaste | null;
  // 口味偏好 (酸味濃 / 清爽酸)
  sourTaste?: TZuchiSourTaste | null;
  // 口味偏好 (重口味 / 清淡)
  taste?: TZuchiTaste | null;

  // 饮食风格
  eatingStyle?: TZuchiEatingStyle[];
};
